import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronRightIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
import { compareListings } from '../data/compareListings';
interface ServiceComparePageProps {
  serviceSlug: string;
  onNavigate: (path: string) => void;
}
export function ServiceComparePage({
  serviceSlug,
  onNavigate
}: ServiceComparePageProps) {
  const listingData = compareListings.find(l => l.serviceSlug === serviceSlug);
  if (!listingData) {
    return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4" data-id="element-2705">
        <h1 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2706">
          Service not found
        </h1>
        <button onClick={() => onNavigate('/')} className="text-amber-600 hover:text-amber-700 font-medium flex items-center gap-2" data-id="element-2707">
          Return Home <ChevronRightIcon className="w-4 h-4" data-id="element-2708" />
        </button>
      </div>;
  }
  const getHeroImage = () => {
    const images: Record<string, string> = {
      'window-replacement': 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=800&q=80',
      roofing: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=800&q=80',
      'bathroom-remodel': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=800&q=80',
      'kitchen-remodel': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80'
    };
    return images[serviceSlug] || 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=80';
  };
  return <div className="min-h-screen bg-[#F4F7F6] font-sans pb-16" data-id="element-2709">
      {/* Breadcrumb */}
      <div className="bg-white py-2.5 border-b border-gray-200" data-id="element-2710">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-xs font-medium text-gray-500" data-id="element-2711">
          <button onClick={() => onNavigate('/')} className="hover:text-slate-900" data-id="element-2712">
            Home
          </button>
          <span className="mx-2" data-id="element-2713">/</span>
          <button onClick={() => onNavigate(`/near-me/${serviceSlug}`)} className="hover:text-slate-900" data-id="element-2714">
            {listingData.serviceName}
          </button>
          <span className="mx-2" data-id="element-2715">/</span>
          <span className="text-slate-900" data-id="element-2716">Best</span>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-id="element-2717">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-6 mb-8 items-center" data-id="element-2718">
          <div className="flex-1" data-id="element-2719">
            <h1 className="text-3xl md:text-4xl font-serif text-slate-900 mb-3 leading-tight" data-id="element-2720">
              Best {listingData.serviceName} Of 2026
            </h1>
            <p className="text-base text-slate-600 mb-4 leading-relaxed" data-id="element-2721">
              {listingData.description}
            </p>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500" data-id="element-2722">
              <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-2723">
                <CheckIcon className="w-2.5 h-2.5 text-emerald-600" strokeWidth={3} data-id="element-2724" />
              </div>
              Last Updated Mar 2026
            </div>
          </div>
          <div className="w-full md:w-1/3" data-id="element-2725">
            <img src={getHeroImage()} alt={`${listingData.serviceName} illustration`} className="w-full h-48 object-cover rounded-xl shadow-sm" data-id="element-2726" />
          </div>
        </div>

        {/* Listings */}
        <div className="space-y-4" data-id="element-2727">
          {listingData.companies.map((company, index) => <motion.div key={company.name} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.08
        }} className={`bg-white rounded-xl shadow-sm border overflow-hidden ${company.rank === 1 ? 'border-amber-500 ring-1 ring-amber-500/20' : 'border-slate-200'}`} data-id="element-2728">
              {/* #1 Banner */}
              {company.rank === 1 && <div className="bg-amber-500 text-white text-center py-1.5 font-bold text-xs flex items-center justify-center gap-1.5 tracking-wide" data-id="element-2729">
                  <span data-id="element-2730">🏆</span> #1 Recommended {listingData.serviceName}
                </div>}

              {/* Card Content */}
              <div className="flex flex-col md:flex-row" data-id="element-2731">
                {/* Left: Info & Features */}
                <div className="flex-1 p-4 md:p-5" data-id="element-2732">
                  {/* Offer badge */}
                  <div className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-[11px] font-bold px-2.5 py-1 rounded-md mb-3" data-id="element-2733">
                    <StarIcon className="w-3 h-3 fill-current" data-id="element-2734" />
                    {company.offer}
                  </div>

                  {/* Company name row */}
                  <div className="flex items-center gap-2.5 mb-3" data-id="element-2735">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 font-bold text-sm flex-shrink-0" data-id="element-2736">
                      {company.rank}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight" data-id="element-2737">
                      {company.name}
                    </h3>
                  </div>

                  {/* Features in 2 columns */}
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-id="element-2738">
                    {company.features.map((feature, idx) => <li key={idx} className="flex items-start gap-1.5 text-[13px] text-slate-600 py-0.5" data-id="element-2739">
                        <CheckIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-2740" />
                        <span className="leading-snug" data-id="element-2741">{feature}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Right: Logo & CTA */}
                <div className="w-full md:w-48 lg:w-56 flex flex-col items-center justify-center gap-3 p-4 md:p-5 md:border-l border-t md:border-t-0 border-slate-100 bg-slate-50/50" data-id="element-2742">
                  {company.logoUrl ? <img src={company.logoUrl} alt={`${company.name} logo`} className="h-12 w-auto object-contain" data-id="element-2743" /> : <span className="font-bold text-base text-slate-700 text-center" data-id="element-2744">
                      {company.name}
                    </span>}

                  <button onClick={() => onNavigate(company.partnerRoute || `/${serviceSlug}/quote-form`)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-5 rounded-lg transition-all text-sm shadow-md shadow-emerald-600/25 hover:shadow-lg hover:shadow-emerald-600/30 hover:scale-[1.02] flex items-center justify-center gap-1.5" data-id="element-2745">
                    See Details
                    <ArrowRightIcon className="w-4 h-4" data-id="element-2746" />
                  </button>

                  <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1" data-id="element-2747">
                    <StarIcon className="w-3 h-3 fill-current" data-id="element-2748" />
                    {company.offer}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </div>
      </main>
    </div>;
}